import { FormEvent, useState } from 'react'
import { Copy, KeyRound, RefreshCw, Search, UserPlus } from 'lucide-react'
import { Alerta, Badge, Boton, Campo, Cargando, Entrada, Panel, Tabla } from '../../components/kit'
import { useConsultaPuente, useImportarFactos, useIntegraciones, usePermisosPuente, useResumenFactos } from '../../queries/useIntegraciones'
import { mensajeDeError } from '../../utils/errores'

function Estado({ ok }: { ok: boolean }) {
  return ok ? <Badge tono="exito" punto>Configurado</Badge> : <Badge tono="error" punto>Sin configurar</Badge>
}

/** Solo staff: verificar un despliegue desde la interfaz, sin entrar al servidor. */
export default function Integraciones() {
  const estado = useIntegraciones()
  const consulta = useConsultaPuente()
  const permisos = usePermisosPuente()
  const [rut, setRut] = useState('')
  const [copiado, setCopiado] = useState(false)

  if (estado.isLoading) return <Cargando />
  if (estado.isError || !estado.data) return <Alerta tipo="error">{mensajeDeError(estado.error)}</Alerta>
  const { vti, puente, factos } = estado.data

  const consultar = (e: FormEvent) => {
    e.preventDefault()
    const cuerpo = parseInt(rut.replace(/\./g, '').split('-')[0], 10)
    if (!Number.isNaN(cuerpo)) consulta.mutate(cuerpo)
  }

  const copiar = async () => {
    await navigator.clipboard.writeText(vti.callback)
    setCopiado(true)
    setTimeout(() => setCopiado(false), 2000)
  }

  return (
    <div className="pila pila--amplia">
      <header className="pagina__cabecera">
        <h1 className="dcc-t-heading-m">Integraciones</h1>
      </header>

      <Panel titulo="Login por portal (U-Pasaporte)" acciones={<Estado ok={vti.configurado} />}>
        <dl className="datos">
          <dt>Portal</dt>
          <dd>{vti.portal}</dd>
          <dt>Identificador de la app</dt>
          <dd>{vti.app || '—'}</dd>
          <dt>Nonce</dt>
          <dd>{vti.exige_nonce ? 'Exigido (recomendado)' : 'Opcional — modo compatible'}</dd>
          <dt>Alta de cuentas</dt>
          <dd>{vti.crea_usuarios ? 'Automática al entrar' : 'Solo usuarios registrados (sección Usuarios)'}</dd>
          <dt>Endpoint a registrar</dt>
          <dd className="datos__con-accion">
            <code>{vti.callback}</code>
            <Boton variante="text" tam="sm" icono={Copy} onClick={copiar}>{copiado ? 'Copiado' : 'Copiar'}</Boton>
          </dd>
        </dl>
        {!vti.configurado && (
          <Alerta tipo="info" titulo="Para activarlo">
            Pide a ati@dcc.uchile.cl un identificador y un secreto para este ambiente, registrando el endpoint de arriba, y
            define VTI_APP y VTI_JWT_SECRET en .docker/app/.env.
          </Alerta>
        )}
      </Panel>

      <Panel titulo="Puente (datos de UCampus)" acciones={<Estado ok={puente.configurado} />}>
        <div className="pila">
          <p>
            Puente es el acceso del DCC a la API de UCampus: la app le consulta con su token y Puente decide qué endpoints y qué
            campos le entrega. Cada consulta gasta cuota de UCampus (1500 por hora, compartida).
          </p>
          {puente.configurado ? (
            <p>
              Servicio: <code>{puente.url}</code>
            </p>
          ) : (
            <Alerta tipo="info" titulo="Para activarlo">
              Pide un token para la app a quien administra Puente y define PUENTE_URL y PUENTE_TOKEN en .docker/app/.env (ver
              docs/PUENTE.md).
            </Alerta>
          )}

          <div>
            <Boton variante="outlined" icono={KeyRound} cargando={permisos.isPending} disabled={!puente.configurado} onClick={() => permisos.mutate()}>
              Ver permisos del token
            </Boton>
          </div>
          {permisos.isError && <Alerta tipo="error">{mensajeDeError(permisos.error)}</Alerta>}
          {permisos.data &&
            (permisos.data.acceso_completo ? (
              <Alerta tipo="info" titulo="Acceso completo">
                El token «{permisos.data.usuario}» pasa sin restricciones: recibe todos los endpoints y todos los campos. Para
                producción conviene pedir que lo acoten a lo que la app usa.
              </Alerta>
            ) : (
              <Tabla etiqueta="Endpoints permitidos al token">
                <thead>
                  <tr>
                    <th scope="col">Endpoint</th>
                    <th scope="col">Campos que entrega</th>
                  </tr>
                </thead>
                <tbody>
                  {permisos.data.endpoints.map((p) => (
                    <tr key={`${p.grupo}-${p.endpoint}`}>
                      <td>
                        <code>{p.endpoint}</code>
                      </td>
                      <td>{p.campos.join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Tabla>
            ))}

          <form className="fila" onSubmit={consultar}>
            <Campo etiqueta="Probar con un RUT" ayuda="Devuelve solo los campos que la app guarda.">
              <Entrada value={rut} onChange={(e) => setRut(e.target.value)} placeholder="12.345.678-5" inputMode="numeric" required />
            </Campo>
            <Boton type="submit" variante="outlined" icono={Search} cargando={consulta.isPending} disabled={!puente.configurado}>
              Consultar
            </Boton>
          </form>
          {consulta.isError && <Alerta tipo="error">{mensajeDeError(consulta.error)}</Alerta>}
          {consulta.data && (
            <>
              <Tabla etiqueta="Datos de la persona según UCampus">
                <thead>
                  <tr>
                    <th scope="col">Campo</th>
                    <th scope="col">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(consulta.data.guardables).map(([campo, valor]) => (
                    <tr key={campo}>
                      <td>{campo}</td>
                      <td>{valor === null || valor === '' ? '—' : String(valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </Tabla>
              <p className="texto-secundario">Campos que entregó Puente: {consulta.data.campos_recibidos.join(', ')}.</p>
            </>
          )}
        </div>
      </Panel>
      <PanelFactos configurado={factos.configurado} url={factos.url} />
    </div>
  )
}

/** Factos: el registro de personas del DCC. De ahí se da cuenta (sin roles) a quienes trabajan en el DCC. */
function PanelFactos({ configurado, url }: { configurado: boolean; url: string }) {
  const resumen = useResumenFactos()
  const importar = useImportarFactos()
  const [confirmando, setConfirmando] = useState(false)
  const datos = importar.data ?? resumen.data
  const faltan = datos ? datos.trabajan - datos.trabajan_con_cuenta : 0
  const hecho = importar.data?.importadas

  return (
    <Panel titulo="Factos (personas del DCC)" acciones={<Estado ok={configurado} />}>
      <div className="pila">
        <p>
          Factos es el registro de personas del DCC. De ahí se da cuenta en esta app a quienes trabajan en el DCC, para que puedan
          entrar con U-Pasaporte y elegirse en una ficha de teletrabajo. Las cuentas nuevas no tienen roles; las que ya existen
          conservan los suyos y las desactivadas no se reactivan.
        </p>
        {configurado ? (
          <p>
            Servicio: <code>{url}</code>
          </p>
        ) : (
          <Alerta tipo="info" titulo="Para activarlo">
            Pide un token para la app a quien administra Factos y define FACTOS_URL y FACTOS_TOKEN en .docker/app/.env (ver
            docs/FACTOS.md).
          </Alerta>
        )}
        <div className="acciones-pagina">
          <Boton variante="outlined" icono={RefreshCw} cargando={resumen.isPending} disabled={!configurado} onClick={() => resumen.mutate()}>
            Consultar Factos
          </Boton>
          {datos && !confirmando && (
            <Boton icono={UserPlus} disabled={!faltan} onClick={() => setConfirmando(true)}>
              Registrar {faltan === 1 ? '1 cuenta' : `${faltan} cuentas`}
            </Boton>
          )}
        </div>
        {(resumen.isError || importar.isError) && <Alerta tipo="error">{mensajeDeError(importar.error ?? resumen.error)}</Alerta>}
        {datos && (
          <p>
            {datos.personas} personas en Factos; {datos.trabajan} trabajan en el DCC y {datos.trabajan_con_cuenta} ya tienen cuenta.
          </p>
        )}
        {confirmando && (
          <Alerta tipo="info" titulo={`¿Registrar ${faltan === 1 ? '1 cuenta' : `${faltan} cuentas`}?`}>
            <div className="pila">
              <span>Podrán entrar con U-Pasaporte y verán Inicio y las fichas de los QR hasta que se les den roles en Usuarios.</span>
              <div className="acciones-pagina">
                <Boton tam="sm" variante="text" onClick={() => setConfirmando(false)}>Cancelar</Boton>
                <Boton tam="sm" cargando={importar.isPending} onClick={() => importar.mutate(undefined, { onSettled: () => setConfirmando(false) })}>
                  Registrar
                </Boton>
              </div>
            </div>
          </Alerta>
        )}
        {hecho && (
          <Alerta tipo="success" titulo="Listo">
            {hecho.creadas} nuevas, {hecho.actualizadas} completadas, {hecho.sin_cambios} sin cambios
            {hecho.omitidas ? `, ${hecho.omitidas} omitidas (desactivadas o con el RUT en otra cuenta)` : ''}.
          </Alerta>
        )}
      </div>
    </Panel>
  )
}
